import { useRef, useState } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { parseReceipt, isPaywallError } from '../lib/api';
import { usePaywall } from '../context/PaywallContext';
import { downscaleImage } from '../lib/image';
import { friendlyError } from '../lib/errorReporting';

// Photos straight off a phone camera are often 4–12 MB; the model reads a
// receipt just as well at ~1600px (see lib/image.js).
// Turns a /parse-receipt response into TransactionDialog form values.
// Never auto-saves — the dialog opens pre-filled for the user to confirm.
export function receiptToForm({ parsed, category_matched }) {
  return {
    amount: parsed.amount > 0 ? String(parsed.amount) : '',
    type: parsed.type === 'income' ? 'income' : 'expense',
    category_id: category_matched?.id || '',
    description: parsed.description || '',
    // Noon local time so a date-only value never shifts a day across timezones.
    date: parsed.date ? new Date(`${parsed.date}T12:00:00`) : new Date(),
  };
}

// Photo-of-a-receipt entry point. `variant`:
//  - "icon": round button sized to sit next to Agregar / the mic (Dashboard)
//  - "pill": small labeled pill inside the transaction form
export default function ReceiptScanButton({ onParsed, variant = 'icon', testId = 'receipt-scan-btn' }) {
  const { openPaywall } = usePaywall();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same photo
    if (!file) return;
    setBusy(true);
    try {
      const result = await parseReceipt(await downscaleImage(file, 1600, 'recibo.jpg'));
      if (!(result?.parsed?.amount > 0)) {
        toast.message('No pudimos leer el total — complétalo a mano.');
      }
      onParsed(receiptToForm(result));
    } catch (err) {
      if (isPaywallError(err)) {
        openPaywall(err.response.data.detail);
      } else {
        toast.error(friendlyError(err, 'No se pudo leer el recibo'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFile}
        data-testid={`${testId}-input`}
      />
      {variant === 'pill' ? (
        <button
          type="button"
          data-testid={testId}
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="hit-44 inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-xs font-medium bg-white/[0.05] text-slate-300 hover:bg-white/10 transition-colors active:scale-[0.97] disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" strokeWidth={1.75} />}
          {busy ? 'Leyendo recibo…' : 'Escanear recibo'}
        </button>
      ) : (
        <button
          type="button"
          data-testid={testId}
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          title="Escanear recibo"
          aria-label="Escanear recibo"
          className={variant === 'square'
            ? 'w-14 h-14 shrink-0 rounded-2xl flex items-center justify-center bg-white/[0.06] hover:bg-white/10 text-slate-300 transition-colors active:scale-[0.95] disabled:opacity-60'
            : 'w-11 h-11 rounded-full flex items-center justify-center bg-white/[0.06] hover:bg-white/10 text-violet-300 transition-colors active:scale-[0.95] disabled:opacity-60'}
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" strokeWidth={1.75} />}
        </button>
      )}
    </>
  );
}
